import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Input, Button, Spin, message } from 'antd';
import { SendOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { Service, OpenAPI } from '@/apis';
import styles from './ChatDetail.module.less';

/** 标记该会话为管理员已读（后端未读计数会清零） */
async function markChatSessionRead(sessionId: string): Promise<void> {
  const base = OpenAPI.BASE || '';
  const token = typeof OpenAPI.TOKEN === 'string' ? OpenAPI.TOKEN : localStorage.getItem('token');
  await fetch(`${base}/api/back/chatMessage/sessions/read`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: JSON.stringify({ sessionId }),
  });
}

const POLL_INTERVAL = 3000;

/** 将时间戳格式化为会话中显示的时间 */
function formatMessageTime(createTime: number | string | undefined): string {
  if (createTime == null || createTime === '') return '';
  const ts = typeof createTime === 'number' && createTime < 1e12 ? createTime * 1000 : Number(createTime);
  if (Number.isNaN(ts)) return '';
  const d = new Date(ts);
  return d.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export type ChatMessage = {
  id?: string | number;
  content: string;
  role?: 'customer' | 'admin' | 'user' | 'manager';
  sender?: string;
  createTime?: number | string;
  isAdmin?: boolean;
};

const normalizeMessages = (raw: unknown): ChatMessage[] => {
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : (raw as Record<string, unknown>).list as unknown[] | undefined;
  const dataArr = (raw as Record<string, unknown>).data as unknown[] | undefined;
  const arr = list ?? dataArr ?? [];
  const adminFromValues = ['admin', 'manager', 'server', 'service', 'staff'];
  const clientFromValues = ['client', 'customer', 'user'];
  return arr.map((m: unknown) => {
    const item = m as Record<string, unknown>;
    const data = (item.data as Record<string, unknown>) || item;
    const from = ((data.from as string) ?? (item.from as string) ?? '').toLowerCase();
    const role = ((data.role as string) ?? (item.role as string) ?? '').toLowerCase();
    const isAdminByRole = role === 'admin' || role === 'manager' || Boolean(data.isAdmin);
    const isAdminByFrom = from && adminFromValues.includes(from);
    const isClientByFrom = from && clientFromValues.includes(from);
    const isAdmin = isAdminByRole || isAdminByFrom || (Boolean(data.isAdmin) && !isClientByFrom);
    return {
      id: (data.id as string) ?? item.id,
      content: (data.content as string) ?? (item.content as string) ?? '',
      role: isAdmin ? 'admin' : 'customer',
      createTime: (data.createTime as number) ?? item.createTime,
      isAdmin,
    } as ChatMessage;
  });
};

const ChatDetail: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState('');
  const [leaving, setLeaving] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const listRef = useRef<HTMLDivElement>(null);

  const sid = sessionId ? decodeURIComponent(sessionId) : '';

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  /** 返回列表前标记当前会话已读，再跳转 */
  const handleBack = useCallback(async () => {
    if (!sid) {
      navigate('/cms/chat');
      return;
    }
    setLeaving(true);
    try {
      await markChatSessionRead(sid);
    } finally {
      setLeaving(false);
      navigate('/cms/chat');
    }
  }, [sid, navigate]);

  const fetchMessages = useCallback(async () => {
    if (!sid) return;
    try {
      const res = await Service.ycChatMessageControllerGetMessages(sid);
      const raw = (res as { data?: unknown })?.data;
      setMessages(normalizeMessages(raw));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [sid]);

  useEffect(() => {
    if (!sid) {
      setLoading(false);
      return;
    }
    markChatSessionRead(sid).catch(() => {});
    fetchMessages();
    const timer = setInterval(fetchMessages, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [sid, fetchMessages]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const content = input.trim();
    if (!content || !sid) return;
    setSending(true);
    try {
      await Service.ycChatMessageControllerReply({ sessionId: sid, content });
      setInput('');
      await fetchMessages();
    } catch (e) {
      message.error('发送失败');
    } finally {
      setSending(false);
    }
  };

  if (!sid) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 24 }}>缺少会话 ID</div>
        <Button type="link" onClick={() => navigate('/cms/chat')}>返回会话列表</Button>
      </Card>
    );
  }

  return (
    <div className={styles.chatDetail}>
      <Card
        title={
          <span className={styles.header}>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack} loading={leaving} />
            <span className={styles.sessionId}>会话：{sid}</span>
            <span className={styles.currentTime}>
              当前时间：{currentTime.toLocaleString('zh-CN', { hour12: false })}
            </span>
          </span>
        }
        className={styles.card}
      >
        <div className={styles.mainLayout}>
          <div className={styles.phoneFrame}>
            <div ref={listRef} className={styles.messageList}>
            {loading && messages.length === 0 ? (
              <div className={styles.loading}>
                <Spin tip="加载消息..." />
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div
                  key={msg.id ?? idx}
                  className={msg.isAdmin ? styles.bubbleAdmin : styles.bubbleCustomer}
                >
                  <div className={styles.bubbleLabel}>{msg.isAdmin ? '客服' : '客户'}</div>
                  <div className={styles.bubbleContent}>{msg.content}</div>
                  <div className={styles.bubbleTime}>
                    {formatMessageTime(msg.createTime) || '时间未知'}
                  </div>
                </div>
            ))
          )}
            </div>
          </div>
          <div className={styles.inputColumn}>
            <Input.TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入回复内容..."
              autoSize={{ minRows: 4, maxRows: 12 }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              className={styles.input}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={sending}
              disabled={!input.trim()}
              className={styles.sendBtn}
            >
              发送
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ChatDetail;
